import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { ContentProvider } from "@/content/ContentProvider";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import AITools from "@/pages/AITools";
import Workshops from "@/pages/Workshops";
import Impact from "@/pages/Impact";
import Team from "@/pages/Team";
import Contact from "@/pages/Contact";
import PartnershipRequest from "@/pages/contact/PartnershipRequest";
import WorkshopRequest from "@/pages/contact/WorkshopRequest";
import GeneralContactRequest from "@/pages/contact/GeneralContactRequest";
import VolunteerRequest from "@/pages/contact/VolunteerRequest";
import V2FeedbackRequest from "@/pages/contact/V2FeedbackRequest";
import V2Preview from "@/pages/V2Preview";
import BuiltWithEmergent from "@/pages/BuiltWithEmergent";
import Articles from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import ToolDetail from "@/pages/ToolDetail";
import NotFound from "@/pages/NotFound";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Support from "@/pages/support/Support";
import SupportRequest from "@/pages/support/SupportRequest";
import Community from "@/pages/Community";

import { AdminAuthProvider } from "@/admin/AdminAuthContext";
import { ThemeProvider } from "@/theme/ThemeContext";
import { FavoritesProvider } from "@/favorites/FavoritesContext";
import AdminLogin from "@/admin/AdminLogin";
import AdminAuthCallback from "@/admin/AdminAuthCallback";
import AdminProtectedRoute from "@/admin/AdminProtectedRoute";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminRequestsList from "@/admin/AdminRequestsList";
import AdminRequestDetail from "@/admin/AdminRequestDetail";
import AdminToolsList from "@/admin/AdminToolsList";
import AdminToolEditor from "@/admin/AdminToolEditor";
import AdminQuickPicks from "@/admin/AdminQuickPicks";
import AdminArticlesList from "@/admin/AdminArticlesList";
import AdminArticleEditor from "@/admin/AdminArticleEditor";
import AdminNewsletterList from "@/admin/AdminNewsletterList";
import AdminAccount from "@/admin/AdminAccount";

import { UserAuthProvider } from "@/user/UserAuthContext";
import UserProtectedRoute from "@/user/UserProtectedRoute";
import AuthLogin from "@/pages/auth/AuthLogin";
import AuthSignup from "@/pages/auth/AuthSignup";
import AuthCallback from "@/pages/auth/AuthCallback";
import AuthGoogleComplete from "@/pages/auth/AuthGoogleComplete";
import Account from "@/pages/auth/Account";

// Google Analytics 4 — init runs once at module load; AnalyticsBridge
// must live INSIDE BrowserRouter so it can subscribe to react-router
// location changes for the SPA page_view fan-out.
import { initAnalytics, AnalyticsBridge } from "@/analytics/ga";
import { RouteErrorBoundary } from "@/components/ErrorBoundary";

initAnalytics();

/**
 * Forward any `/en` or `/en/...` request to the canonical English root.
 * English is now mounted at `/` (no language prefix), so `/en/about` becomes
 * `/about`, `/en` becomes `/`. Query strings and hashes are preserved.
 */
function ForwardEnglishToRoot() {
  const { pathname, search, hash } = useLocation();
  const stripped = pathname.replace(/^\/en(?=\/|$)/, "") || "/";
  return <Navigate to={`${stripped}${search}${hash}`} replace />;
}

/**
 * Language-aware fallback for unknown `/tools/*` (and `/ar/tools/*`) URLs.
 *
 * The dedicated `/tools/:slug` route handles real tool slugs via
 * `<ToolDetail />` — when the slug exists it renders the page, when it
 * doesn't `<ToolDetail>` itself sends visitors back to the tools listing.
 * This catch-all is kept as a defence-in-depth so any deeper-nested
 * `/tools/foo/bar` URL still lands on the listing instead of falling
 * through to the global 404.
 */
function RedirectToToolsList() {
  const { pathname } = useLocation();
  const isArabic = pathname.startsWith("/ar/") || pathname === "/ar";
  return <Navigate to={isArabic ? "/ar/tools" : "/tools"} replace />;
}

/**
 * All localised pages are rendered under either:
 *   /         (English — canonical root, no prefix)
 *   /ar/*     (Arabic)
 * The LanguageProvider derives `lang` directly from the URL pathname.
 * AR and EN are two fully separate route trees — no in-place text swap.
 */
function LocalizedRoutes() {
  return (
    <LanguageProvider>
      <UserAuthProvider>
        <FavoritesProvider>
          <Routes>
            <Route element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="tools" element={<AITools />} />
            {/* Phase 19 — per-tool landing pages.
                The dedicated `:slug` route handles real tool slugs; the
                trailing wildcard route catches any deeper or unknown
                `/tools/...` URL and bounces it back to the listing so
                old bookmarks or miswritten links never 404. */}
            <Route path="tools/:slug" element={<ToolDetail />} />
            <Route path="tools/*" element={<RedirectToToolsList />} />
            <Route path="workshops" element={<Workshops />} />
            <Route path="impact" element={<Impact />} />
            <Route path="team" element={<Team />} />
            <Route path="contact" element={<Contact />} />
            <Route path="contact/partnership" element={<PartnershipRequest />} />
            <Route path="contact/host-workshop" element={<WorkshopRequest />} />
            <Route path="contact/general" element={<GeneralContactRequest />} />
            <Route path="contact/volunteer" element={<VolunteerRequest />} />
            {/* Phase 24 — SAS V2 community feedback route + preview article. */}
            <Route path="contact/v2-feedback" element={<V2FeedbackRequest />} />
            <Route path="v2" element={<V2Preview />} />
            <Route path="articles" element={<Articles />} />
            <Route path="articles/:slug" element={<ArticleDetail />} />
            <Route path="built-with-emergent" element={<BuiltWithEmergent />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="terms" element={<Terms />} />
            <Route path="support" element={<Support />} />
            <Route path="support/request" element={<SupportRequest />} />
            <Route path="community" element={<Community />} />

            {/* public auth pages (inside localized tree so RTL/LTR is correct) */}
            <Route path="auth/login" element={<AuthLogin />} />
            <Route path="auth/signup" element={<AuthSignup />} />
            <Route path="auth/callback" element={<AuthCallback />} />
            <Route path="auth/complete-google" element={<AuthGoogleComplete />} />

            {/* protected account shell */}
            <Route element={<UserProtectedRoute />}>
              <Route path="account" element={<Account />} />
            </Route>

            {/* unknown sub-path within a valid lang → localised 404 page
                (renders in the active language's direction: RTL for /ar/…, LTR
                for /en/…). The 404 keeps the visitor on the original URL so
                they can see what they tried to reach. */}
            <Route path="*" element={<NotFound />} />
          </Route>
          </Routes>
        </FavoritesProvider>
      </UserAuthProvider>
    </LanguageProvider>
  );
}

/**
 * Admin routes live outside the /ar /en public trees. The admin UI is
 * English-only by design for this phase and uses its own AuthProvider so the
 * public site pays zero cost for the admin system.
 */
function AdminRoutes() {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="login" element={<AdminLogin />} />
        <Route path="callback" element={<AdminAuthCallback />} />
        <Route element={<AdminProtectedRoute />}>
          <Route element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="requests" element={<AdminRequestsList />} />
            <Route path="requests/:id" element={<AdminRequestDetail />} />
            <Route path="tools" element={<AdminToolsList />} />
            <Route path="tools/quick-picks" element={<AdminQuickPicks />} />
            <Route path="tools/new" element={<AdminToolEditor />} />
            <Route path="tools/:id" element={<AdminToolEditor />} />
            <Route path="articles" element={<AdminArticlesList />} />
            <Route path="articles/new" element={<AdminArticleEditor />} />
            <Route path="articles/:id" element={<AdminArticleEditor />} />
            <Route path="newsletter" element={<AdminNewsletterList />} />
            <Route path="account" element={<AdminAccount />} />
            {/* Future protected sections will mount here as siblings. */}
          </Route>
        </Route>
        {/* Any unknown /admin/* sub-path sends the visitor to login; the
            ProtectedRoute will then promote them to /admin if already signed in. */}
        <Route path="*" element={<Navigate to="/admin/login" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <ContentProvider>
            {/* Fires GA4 page_view on every route change. Safe no-op in
                dev / when Measurement ID is unset. Placed INSIDE the
                BrowserRouter so useLocation() resolves; placed OUTSIDE
                the per-locale provider trees so the same instance runs
                across /, /ar, and /admin. */}
            <AnalyticsBridge />
            {/* P1 stability: app-scoped error boundary. Any uncaught render
                error below this point renders a bilingual full-page
                fallback with a reload action instead of a blank screen.
                Resets automatically when the route changes. */}
            <RouteErrorBoundary scope="app">
            <Routes>
              {/* Admin (separate tree, no localisation) — most specific first. */}
              <Route path="/admin/*" element={<AdminRoutes />} />

              {/* Arabic public tree lives under /ar. */}
              <Route path="/ar" element={<LocalizedRoutes />} />
              <Route path="/ar/*" element={<LocalizedRoutes />} />

              {/* Legacy `/en` and `/en/...` paths redirect cleanly to the new
                  English root (`/...`). Preserves query strings + hashes so
                  deep links keep working. */}
              <Route path="/en" element={<ForwardEnglishToRoot />} />
              <Route path="/en/*" element={<ForwardEnglishToRoot />} />

              {/* Default tree → English at root. Includes `/`, `/about`,
                  `/tools`, etc. Unknown paths render the English 404 inside
                  the localised tree (no separate forwarder needed). */}
              <Route path="/*" element={<LocalizedRoutes />} />
            </Routes>
            </RouteErrorBoundary>
          </ContentProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
